import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../../contexts/AuthContext";
import { getAccessToken } from "../../../awsAuth";
import "./ShowcaseDetails.scss";
import { useToast } from "../../Alerts/Toast";

import backIcon from "../img/back.png";
import mapIcon from "../img/map.png";
import moreIcon from "../img/more.png";
import chatIcon from "../img/chat.png";
import callIcon from "../img/call.png";
import editIcon from "../img/edit.png";
// import announceIcon from "../img/announce.png";

import ShowcaseActions from "./ShowcaseActions";
import PhotoDetails from "../../PhotoDetails/PhotoDetails";

// import ShowcaseAnnouncement from "./ShowcaseAnnouncement";

import descriptionTitleIcon from "../img/description.png";
import workingHoursTitleIcon from "../img/working-hours.png";
import processTitleIcon from "../img/process.png";
import serviceTitleIcon from "../img/service.png";
import experienceTitleIcon from "../img/experiense.png";
import formatTitleIcon from "../img/format.png";
import promotionTitleIcon from "../img/promotion.png";
import faqTitleIcon from "../img/faq.png";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

/* ------------------------------ small utils ------------------------------ */
function safeObj(v) {
  return v && typeof v === "object" ? v : {};
}

async function buildAuthHeaders() {
  const raw = await Promise.resolve(getAccessToken());
  const token = raw ? String(raw).trim() : "";
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function formatUaDate(ts) {
  const n = Number(ts);
  if (!n || Number.isNaN(n)) return "";
  try {
    return new Intl.DateTimeFormat("uk-UA", { day: "2-digit", month: "long", year: "numeric" }).format(new Date(n));
  } catch {
    try {
      return new Date(n).toLocaleDateString("uk-UA");
    } catch {
      return "";
    }
  }
}

function formatUahPrice(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const digits = s.replace(/[^\d]/g, "");
  if (!digits) return "";
  const n = Number(digits);
  if (!Number.isFinite(n) || n <= 0) return "";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "UAH" }).format(n);
  } catch {
    return `UAH ${n}`;
  }
}

/* --------------------------- core normalization -------------------------- */
function normalizeCoreForServices(core) {
  const c = safeObj(core);
  const modeRaw = String(c.mode || "").trim();
  const mode = modeRaw === "online" || modeRaw === "offline" || modeRaw === "both" ? modeRaw : "both";

  const servicesRaw = Array.isArray(c.services) ? c.services : [];
  const services = servicesRaw
    .map((s) => {
      if (typeof s === "string") return s;
      if (s && typeof s === "object") return String(s.name || "").trim();
      return "";
    })
    .filter(Boolean);

  const loc = safeObj(c.location);
  const location = {
    city: String(loc.city || ""),
    address: String(loc.address || ""),
    note: String(loc.note || ""),
    text: String(loc.text || ""),
  };

  const offers = Array.isArray(c.offers)
    ? c.offers
    : Array.isArray(c.promos)
      ? c.promos.map((p) => ({ title: p?.title || "", details: p?.text || "" }))
      : [];

  const contactsObj = safeObj(c.contacts);
  const contacts = {
    showPhone: typeof contactsObj.showPhone === "boolean" ? contactsObj.showPhone : undefined,
    phone: String(contactsObj.phone || ""),
    telegram: String(contactsObj.telegram || ""),
    instagram: String(contactsObj.instagram || ""),
    website: String(contactsObj.website || ""),
  };

  return {
    services,
    location,
    schedule: Array.isArray(c.schedule) ? c.schedule : [],
    offers,
    faq: Array.isArray(c.faq) ? c.faq : [],
    contacts,
    mode,
    ...c,
  };
}

function normalizeExperience(core) {
  const c = safeObj(core);
  const ex = safeObj(c.experience);

  const startYearRaw = String(ex.startYear ?? "").trim().replace(/[^\d]/g, "").slice(0, 4);
  const startYear = startYearRaw ? parseInt(startYearRaw, 10) : 0;

  const clientsRaw = String(ex.clientsCount ?? "").trim().replace(/[^\d]/g, "");
  const clientsCount = clientsRaw ? parseInt(clientsRaw, 10) : 0;

  return {
    startYear: Number.isFinite(startYear) ? startYear : 0,
    clientsCount: Number.isFinite(clientsCount) ? clientsCount : 0,
  };
}

/* ---------------------------- gallery normalize --------------------------- */
function normalizeShowcaseGallery(data, core) {
  const d = safeObj(data);
  const c = safeObj(core);
  const trust = safeObj(c.trust);

  const cg = safeObj(c.gallery);
  if (Array.isArray(cg.items) && cg.items.length) {
    const title = String(cg.title || "").trim();
    const items = cg.items
      .map((it) => {
        const o = safeObj(it);
        const url = String(o.url || "").trim();
        if (!url) return null;
        const top = String(o.caption || "").trim();
        const bottom = formatUahPrice(o.price);
        return { url, top, bottom };
      })
      .filter(Boolean);

    const seen = new Set();
    const uniq = [];
    for (const p of items) {
      const key = String(p.url || "");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      uniq.push(p);
    }
    return { title, items: uniq.slice(0, 5) };
  }

  const candidates = [
    d.processPhotos,
    d.workPhotos,
    d.photos,
    d.gallery,
    c.processPhotos,
    c.workPhotos,
    c.photos,
    c.gallery,
    trust.processPhotos,
    trust.workPhotos,
    trust.photos,
    trust.gallery,
  ];

  const arr = candidates.find((x) => Array.isArray(x)) || [];
  const items = (Array.isArray(arr) ? arr : [])
    .map((it) => {
      if (typeof it === "string") {
        const url = String(it || "").trim();
        return url ? { url, top: "", bottom: "" } : null;
      }
      if (it && typeof it === "object") {
        const url = String(it.url || it.src || it.imageUrl || it.photoUrl || it.s3Url || "").trim();
        const top = String(it.caption || it.label || it.text || it.title || "").trim();
        const bottom = formatUahPrice(it.price);
        return url ? { url, top, bottom } : null;
      }
      return null;
    })
    .filter(Boolean);

  const seen = new Set();
  const uniq = [];
  for (const p of items) {
    const key = String(p.url || "");
    if (!key || seen.has(key)) continue;
    seen.add(key);
    uniq.push(p);
  }

  return { title: "", items: uniq.slice(0, 5) };
}

function isShowcaseEmpty(data) {
  if (!data) return true;

  const descOk = !!String(data.description || "").trim();
  const coverOk = !!String(data.coverUrl || "").trim();

  const c = normalizeCoreForServices(data.core || {});
  const servicesOk = (c.services || []).some((s) => s && !!String(s).trim());

  const locationOk =
    !!String(c.location?.text || "").trim() ||
    !!String(c.location?.city || "").trim() ||
    !!String(c.location?.address || "").trim() ||
    !!String(c.location?.note || "").trim();

  const scheduleOk =
    Array.isArray(c.schedule) &&
    c.schedule.some((x) => !!String(x?.day || "").trim() || !!String(x?.from || "").trim() || !!String(x?.to || "").trim());

  const offersOk =
    Array.isArray(c.offers) && c.offers.some((p) => !!String(p?.title || "").trim() || !!String(p?.details || "").trim());

  const faqOk = Array.isArray(c.faq) && c.faq.some((f) => !!String(f?.q || "").trim() || !!String(f?.a || "").trim());

  const galleryOk = normalizeShowcaseGallery(data, c).items.length > 0;

  const ex = normalizeExperience(c);
  const experienceOk = !!ex.startYear || !!ex.clientsCount;

  return !descOk && !coverOk && !servicesOk && !locationOk && !scheduleOk && !offersOk && !faqOk && !galleryOk && !experienceOk;
}

/* -------------------------- schedule status helpers -------------------------- */
const DAY_ALIASES = {
  0: ["нд", "неділя", "sun", "sunday"],
  1: ["пн", "понеділок", "mon", "monday"],
  2: ["вт", "вівторок", "tue", "tues", "tuesday"],
  3: ["ср", "середа", "wed", "wednesday"],
  4: ["чт", "четвер", "thu", "thur", "thurs", "thursday"],
  5: ["пт", "пʼятниця", "п'ятниця", "fri", "friday"],
  6: ["сб", "субота", "sat", "saturday"],
};

function normDayStr(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[–—−]/g, "-")
    .trim();
}

function parseTimeToMin(t) {
  const m = String(t || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

function dayIndexByAlias(token) {
  const v = normDayStr(token);
  for (const idx of Object.keys(DAY_ALIASES)) {
    if (DAY_ALIASES[idx].some((a) => v === a)) return Number(idx);
  }
  return null;
}

function expandDaysFromText(dayText) {
  const d = normDayStr(dayText);
  if (!d) return [];
  if (/(closed|зачинено|вихідн|не працю)/i.test(d)) return [];

  const parts = d.split("-");
  if (parts.length === 2) {
    const a = dayIndexByAlias(parts[0].trim()) ?? null;
    const b = dayIndexByAlias(parts[1].trim()) ?? null;
    if (a !== null && b !== null) {
      const out = [];
      let cur = a;
      while (true) {
        out.push(cur);
        if (cur === b) break;
        cur = (cur + 1) % 7;
        if (out.length > 7) break;
      }
      return out;
    }
  }

  const direct = dayIndexByAlias(d);
  if (direct !== null) return [direct];

  for (let i = 0; i < 7; i++) {
    if (DAY_ALIASES[i].some((a) => d.includes(a))) return [i];
  }
  return [];
}

function getWorkStatus(schedule) {
  const now = new Date();
  const dow = now.getDay();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const rows = (Array.isArray(schedule) ? schedule : [])
    .map((x) => ({ days: expandDaysFromText(x?.day), fromMin: parseTimeToMin(x?.from), toMin: parseTimeToMin(x?.to) }))
    .filter((r) => r.days.length > 0);

  const row = rows.find((r) => r.days.includes(dow)) || null;
  if (!row || row.fromMin === null || row.toMin === null) return { kind: "closed", label: "Закрито зараз" };

  const from = row.fromMin;
  const to = row.toMin;

  if (nowMin < from || nowMin >= to) return { kind: "closed", label: "Закрито зараз" };

  const minsLeft = to - nowMin;
  if (minsLeft <= 30) return { kind: "soon", label: "Скоро закривається" };

  return { kind: "open", label: "Відкрито зараз" };
}

/* ------------------------------ contacts helpers ------------------------------ */
function resolveShowPhone(data, core) {
  const candidates = [data?.showPhone, data?.contacts?.showPhone, core?.showPhone, core?.contacts?.showPhone, core?.contact?.showPhone];
  for (const v of candidates) {
    if (typeof v === "boolean") return v;
  }
  return false;
}

function pickPhone(data, core) {
  const candidates = [
    data?.account?.phone,
    data?.account?.phoneNumber,
    data?.phone,
    data?.phoneNumber,
    core?.phone,
    core?.contacts?.phone,
    core?.contact?.phone,
  ]
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  const raw = candidates[0] || "";
  return raw.replace(/[^\d+]/g, "");
}

/* ========================================================================== */

export default function ShowcaseDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userId: currentUserId } = useAuth();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [author, setAuthor] = useState({
  id: "",
  username: "Користувач",
  avatar: "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  // const [announcementOpen, setAnnouncementOpen] = useState(false);

  const [faqOpenIndex, setFaqOpenIndex] = useState(-1);

  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoStartIndex, setPhotoStartIndex] = useState(0);

  const [timeTick, setTimeTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setTimeTick(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!id) return;
    let alive = true;

    (async () => {
      setBusy(true);
      try {
        setErr("");
        const r = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(id)}`, { cache: "no-store" });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j?.error || "load failed");
        if (!alive) return;
        setData(j);
      } catch {
        if (alive) setErr("Не вдалося завантажити вітрину");
      } finally {
        if (alive) setBusy(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
  const uid = String(data?.userId || "").trim();
  if (!uid) return;

  let alive = true;

  (async () => {
    try {
      const r = await fetch(`${API_BASE}/api/account/${encodeURIComponent(uid)}`, {
        cache: "no-store",
      });

      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "author load failed");
      if (!alive) return;

      setAuthor({
        id: uid,
        username: j?.username || j?.userName || j?.name || "Користувач",
        avatar:
          j?.avatar ||
          j?.avatarUrl ||
          j?.photo ||
          "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png",
      });
    } catch {
      if (!alive) return;

      setAuthor((prev) => ({
        ...prev,
        id: uid,
      }));
    }
  })();

  return () => {
    alive = false;
  };
}, [data?.userId]);

  const isOwner = useMemo(() => !!data && !!currentUserId && String(data.userId) === String(currentUserId), [data, currentUserId]);
  const coreServices = useMemo(() => normalizeCoreForServices(data?.core || {}), [data]);

  const experience = useMemo(() => normalizeExperience(coreServices), [coreServices]);
  const hasExperience = useMemo(() => !!experience.startYear || !!experience.clientsCount, [experience]);

  const showcaseGallery = useMemo(() => normalizeShowcaseGallery(data, coreServices), [data, coreServices]);
  const empty = useMemo(() => isShowcaseEmpty(data), [data]);

  const createdLine = useMemo(() => {
    const d = formatUaDate(data?.createdAt);
    return d ? `Вітрина створена: ${d}` : "";
  }, [data?.createdAt]);

  const modeLabel = useMemo(() => {
    const m = coreServices?.mode || "both";
    if (m === "online") return "Онлайн";
    if (m === "offline") return "Офлайн";
    return "Онлайн/Офлайн";
  }, [coreServices?.mode]);

  const schedule = useMemo(() => (Array.isArray(coreServices?.schedule) ? coreServices.schedule : []), [coreServices]);
  const workStatus = useMemo(() => {
    void timeTick;
    return getWorkStatus(schedule);
  }, [schedule, timeTick]);

  const canShowPhone = useMemo(() => resolveShowPhone(data, coreServices), [data, coreServices]);
  const photoCacheBuster = useMemo(() => encodeURIComponent(String(data?.updatedAt || Date.now())), [data?.updatedAt]);

  const photosForViewer = useMemo(() => {
    const items = Array.isArray(showcaseGallery?.items) ? showcaseGallery.items : [];
    return items
      .map((p, idx) => {
        const u = String(p?.url || "").trim();
        if (!u) return null;
        const top = String(p?.top || "").trim();
        const src = `${u}${u.includes("?") ? "&" : "?"}v=${photoCacheBuster}`;
        return { url: src, alt: top || `photo ${idx + 1}` };
      })
      .filter(Boolean);
  }, [showcaseGallery, photoCacheBuster]);

  const doShare = useCallback(async () => {
    try {
      const url = typeof window !== "undefined" ? window.location.href : "";
      if (!url) throw new Error("no url");

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Посилання скопійовано", 2200);
        return;
      }

      window.prompt("Скопіюй посилання:", url);
      toast.success("Посилання скопійовано", 2200);
    } catch {
      toast.error("Не вдалося скопіювати посилання", 3200);
    }
  }, [toast]);

  const openChatWithPrefill = useCallback(
    (prefillText) => {
      const uid = String(data?.userId || "").trim();
      if (!uid) {
        setErr("Не вдалося відкрити чат");
        toast.error("Не вдалося відкрити чат", 3000);
        return;
      }
      const prefill = String(prefillText || "").trim();

      if (prefill) {
        navigate(`/messages?to=${encodeURIComponent(uid)}&prefill=${encodeURIComponent(prefill)}`);
      } else {
        navigate(`/messages?to=${encodeURIComponent(uid)}`);
      }
    },
    [data?.userId, navigate, toast]
  );

  const onChat = useCallback(() => {
  openChatWithPrefill("");
  }, [openChatWithPrefill]);

  const onBook = useCallback(() => {
    openChatWithPrefill(
      "Вітаю! Звертаюся з вашої вітрини на Cvanto. Хочу записатися/узгодити час. Підкажіть, будь ласка, найближчі вільні слоти та умови (вартість, тривалість, адреса/виїзд)."
    );
  }, [openChatWithPrefill]);

  const goEdit = useCallback(() => navigate(`/showcase/${encodeURIComponent(id)}/edit`), [navigate, id]);

  const onCall = useCallback(() => {
    if (!canShowPhone) {
      setErr("Власник приховав номер телефону");
      toast.info("Власник приховав номер телефону", 2600);
      return;
    }
    const tel = pickPhone(data, coreServices);
    if (!tel) {
      setErr("Номер телефону не вказано");
      toast.info("Номер телефону не вказано", 2600);
      return;
    }
    window.location.href = `tel:${tel}`;
  }, [canShowPhone, data, coreServices, toast]);

  const onDeleteShowcase = useCallback(async () => {
  if (!id) return;

  if (!isOwner) {
    setErr("Ви не можете видалити цю вітрину");
    toast.error("Ви не можете видалити цю вітрину", 3200);
    return;
  }

  const ok = window.confirm("Точно видалити вітрину?");
  if (!ok) return;

  try {
    setErr("");

    const authHeaders = await buildAuthHeaders();

    const res = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      credentials: "include",
    });

    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j?.error || "delete failed");

    setActionsOpen(false);
    toast.success("Вітрину видалено", 2200);
    navigate("/profile", { replace: true });
    return;
      } catch (e) {
        const msg = e?.message || "Не вдалося видалити вітрину";
        setErr(msg);
        toast.error(msg, 3200);
      }
    }, [id, isOwner, navigate, toast]);

  if (busy || data === null) {
    return (
      <div className="showcaseDetails">
        <div className="sdTopBar">
          <button className="sdIconBtn" onClick={() => navigate(-1)} aria-label="Назад" title="Назад">
            <img src={backIcon} alt="" />
          </button>
          <div className="sdTopTitle">…</div>
          <button className="sdIconBtn sdIconBtn--ghost" disabled aria-label="Дії" title="Дії">
            <img src={moreIcon} alt="" />
          </button>
        </div>

        <div className="sdBody">
          <div className="sdCard skel" style={{ height: 240 }} />
          <div className="sdCard skel" style={{ height: 120 }} />
          <div className="sdCard skel" style={{ height: 170 }} />
        </div>
      </div>
    );
  }

  const services = Array.isArray(coreServices?.services) ? coreServices.services : [];
  const offers = Array.isArray(coreServices?.offers) ? coreServices.offers : [];
  const faq = Array.isArray(coreServices?.faq) ? coreServices.faq : [];

  const locCity = String(coreServices?.location?.city || "").trim();
  const locAddress = String(coreServices?.location?.address || "").trim();
  const locNote = String(coreServices?.location?.note || "").trim();
  const locLegacy = String(coreServices?.location?.text || "").trim();

  const locText = (locLegacy || [locCity, locAddress].filter(Boolean).join(", ")).trim();
  const locExtra = locNote;

  const hasServices = services.some((s) => s && String(s).trim());
  const hasSchedule = schedule.some((x) => !!String(x?.day || "").trim() || !!String(x?.from || "").trim() || !!String(x?.to || "").trim());
  const hasOffers = offers.some((o) => !!String(o?.title || "").trim() || !!String(o?.details || "").trim());
  const hasFaq = faq.some((f) => !!String(f?.q || "").trim() || !!String(f?.a || "").trim());
  const hasPhotos = Array.isArray(showcaseGallery?.items) && showcaseGallery.items.length > 0;

  const title = String(data?.title || "Вітрина").trim() || "Вітрина";
  const telForUi = canShowPhone ? pickPhone(data, coreServices) : "";
  const galleryTitle = String(showcaseGallery?.title || "").trim() || "Фото процесу / результатів";

  const contacts = safeObj(coreServices?.contacts);
  const contactPhone = String(contacts?.phone || "").trim();
  const contactTelegram = String(contacts?.telegram || "").trim();
  const contactInstagram = String(contacts?.instagram || "").trim();
  const contactWebsite = String(contacts?.website || "").trim();

  const hasAnyContacts =
    (!!telForUi && canShowPhone) || !!contactPhone || !!contactTelegram || !!contactInstagram || !!contactWebsite;

  const normalizeUrl = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    if (/^https?:\/\//i.test(s)) return s;
    if (/^www\./i.test(s)) return `https://${s}`;
    return s;
  };

  const toInstagramUrl = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    if (/^https?:\/\//i.test(s)) return s;
    const handle = s.replace(/^@/, "");
    return handle ? `https://instagram.com/${handle}` : "";
  };

  const toTelegramUrl = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    if (/^https?:\/\//i.test(s)) return s;
    const handle = s.replace(/^@/, "");
    return handle ? `https://t.me/${handle}` : "";
  };

  const cleanWebsite = normalizeUrl(contactWebsite);

  const WEEK_ORDER = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];
  const scheduleByDay = (() => {
    const map = {};
    for (const row of schedule) {
      const day = String(row?.day || "").trim();
      const from = String(row?.from || "").trim();
      const to = String(row?.to || "").trim();
      if (!day) continue;
      map[day] = { from, to };
    }
    return map;
  })();

  const currentYear = (() => {
    try {
      return new Date().getFullYear();
    } catch {
      return 2026;
    }
  })();

  const yearsExp = experience.startYear ? Math.max(0, currentYear - experience.startYear) : 0;
  const yearsWord = (n) => (n === 1 ? "рік" : n >= 2 && n <= 4 ? "роки" : "років");

  // SEO: both /showcase/:id and /s/:id (a short-link alias, same component)
  // render this page, and neither carried any title/description/canonical
  // before this audit — a real "duplicate URL, no user-selected canonical"
  // source. The canonical always points at the long /showcase/:id form
  // regardless of which route matched, consolidating both onto one URL.
  // Genuinely empty showcases (owner hasn't filled anything in yet) are
  // marked noindex rather than submitted for indexing as thin content.
  const seoUrl = `https://cvanto.com/showcase/${encodeURIComponent(id)}`;
  const seoDescRaw = String(data?.description || "").replace(/\s+/g, " ").trim();
  const seoDesc = (
    seoDescRaw ||
    `Вітрина ${title}${locText ? ` — ${locText}` : ""} на Cvanto.`
  ).slice(0, 160);

  return (
    <div className={`showcaseDetails is-viewer ${isOwner ? "is-owner" : ""}`}>
      <Helmet>
        <title>{`${title} — Cvanto`}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={seoUrl} />
        {empty ? <meta name="robots" content="noindex,follow" /> : null}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cvanto" />
        <meta property="og:title" content={`${title} — Cvanto`} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={seoUrl} />
        {data?.coverUrl ? <meta property="og:image" content={data.coverUrl} /> : null}
      </Helmet>

      <div className="sdTopBar">
        <button className="sdIconBtn" onClick={() => navigate(-1)} aria-label="Назад" title="Назад">
          <img src={backIcon} alt="" />
        </button>

        <div className="sdTopTitle">{title}</div>

        <button className="sdIconBtn" onClick={() => setActionsOpen(true)} aria-label="Дії" title="Дії">
          <img src={moreIcon} alt="" />
        </button>
      </div>

      {false && err && <div className="sdToast">{err}</div>}

      {actionsOpen && (
        <ShowcaseActions
          onClose={() => setActionsOpen(false)}
          isOwner={isOwner}
          onEdit={() => {
            setActionsOpen(false);
            goEdit();
          }}
          onShare={async () => {
            setActionsOpen(false);
            await doShare();
          }}
          onDeleteShowcase={async () => {
            await onDeleteShowcase();
          }}
        />
      )}

      {/*
      {announcementOpen && (
        <ShowcaseAnnouncement
          isOpen={announcementOpen}
          onClose={() => setAnnouncementOpen(false)}
          showcaseId={id}
          showcase={data}
        />
      )}
      */}

      {photoOpen && (
        <PhotoDetails isOpen={photoOpen} photos={photosForViewer} startIndex={photoStartIndex} onClose={() => setPhotoOpen(false)} />
      )}

      <div className="sdBody">
        <div className="sdHeroViber">
          {data.coverUrl ? (
            <img
              className="sdHeroViber__img"
              src={`${data.coverUrl}${data.coverUrl.includes("?") ? "&" : "?"}v=${encodeURIComponent(String(data.updatedAt || Date.now()))}`}
              alt="cover"
            />
          ) : (
            <div className="sdHeroViber__ph">
              <div className="sdHeroViber__phTitle">Обкладинка</div>
              <div className="sdHeroViber__phText">Поки що без фото</div>
            </div>
          )}
          <div className="sdHeroViber__title">{title}</div>
        </div>

        {!!createdLine && <div className="sdCreatedUnderHero">{createdLine}</div>}

        {author?.id ? (
          <button
            type="button"
            className="sdAuthorRow"
            onClick={() => navigate(`/profile/${encodeURIComponent(author.id)}`)}
            aria-label="Перейти на профіль автора"
          >
            <img
              className="sdAuthorRow__avatar"
              src={author.avatar}
              alt={author.username}
              onError={(e) => {
                e.currentTarget.src =
                  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";
              }}
            />

            <div className="sdAuthorRow__meta">
              <div className="sdAuthorRow__label">Автор вітрини</div>
              <div className="sdAuthorRow__name">{author.username}</div>
            </div>
          </button>
        ) : null}

        {empty ? (
          <div className="sdCard">
            <div className="sdCard__title">Вітрина ще поки не заповнена</div>
            <div className="sdText sdText--muted">
              Тут зʼявляться: обкладинка, опис, послуги, локація, графік, фото, акції, досвід та FAQ — після того як власник заповнить.
            </div>
          </div>
        ) : (
          <>
            {!!String(data.description || "").trim() && (
              <div className="sdCard">
                <div className="sdTitleRow">
                  <img className="sdTitleIcon" src={descriptionTitleIcon} alt="" />
                  <div className="sdCard__title">Опис</div>
                </div>
                <div className="sdText">{data.description}</div>
              </div>
            )}

            <div className="sdCard">
              <div className="sdTitleRow">
                <img className="sdTitleIcon" src={chatIcon} alt="" />
                <div className="sdCard__title">Контакти</div>
              </div>

              <div className="sdActionsRow sdActionsRow--insideCard">
                {isOwner ? (
                  <>
                    <button className="sdActionBtn sdActionBtn--ownerEdit" type="button" onClick={goEdit}>
                      <img className="sdNoInvert" src={editIcon} alt="" />
                      <span>Редагувати вітрину</span>
                    </button>

                    {/*
                    <button
                      className="sdActionBtn sdActionBtn--announce"
                      type="button"
                      onClick={() => setAnnouncementOpen(true)}
                    >
                      <img className="sdNoInvert" src={announceIcon} alt="" />
                      <span>Повідомити клієнтів</span>
                    </button>
                    */}
                  </>
                ) : (
                  <>
                    <button className="sdActionBtn" type="button" onClick={onChat}>
                      <img src={chatIcon} alt="" />
                      <span>Написати</span>
                    </button>

                    {canShowPhone && telForUi ? (
                      <button className="sdActionBtn" type="button" onClick={onCall}>
                        <img src={callIcon} alt="" />
                        <span>Зателефонувати</span>
                      </button>
                    ) : null}
                  </>
                )}
              </div>

              {hasAnyContacts ? (
                <div className="sdContactsList">
                  {canShowPhone && telForUi ? (
                    <div className="sdContactRow"></div>
                  ) : null}

                  {!canShowPhone && (contactPhone || telForUi) ? (
                    <div className="sdContactRow">
                      <div className="sdContactLabel">Телефон</div>
                      <div className="sdContactValue sdContactValue--muted">Приховано</div>
                    </div>
                  ) : null}

                  {contactTelegram ? (
                    <div className="sdContactRow">
                      <div className="sdContactLabel">Telegram</div>
                      <a className="sdContactValue" href={toTelegramUrl(contactTelegram)} target="_blank" rel="noreferrer">
                        {contactTelegram}
                      </a>
                    </div>
                  ) : null}

                  {contactInstagram ? (
                    <div className="sdContactRow">
                      <div className="sdContactLabel">Instagram</div>
                      <a className="sdContactValue" href={toInstagramUrl(contactInstagram)} target="_blank" rel="noreferrer">
                        {contactInstagram}
                      </a>
                    </div>
                  ) : null}

                  {cleanWebsite ? (
                    <div className="sdContactRow">
                      <div className="sdContactLabel">Сайт</div>
                      <a className="sdContactValue" href={cleanWebsite} target="_blank" rel="noreferrer">
                        {contactWebsite}
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="sdText sdText--muted">Контактів не додано</div>
              )}
            </div>

            {hasSchedule && (
              <div className={`sdCard sdScheduleCard ${scheduleOpen ? "is-open" : "is-collapsed"}`}>
                <button
                  type="button"
                  className="sdScheduleHead"
                  onClick={() => setScheduleOpen((v) => !v)}
                  aria-expanded={scheduleOpen ? "true" : "false"}
                >
                  <div className="sdScheduleHead__left">
                    <div className="sdScheduleHead__title">
                      <img className="sdTitleIcon sdTitleIcon--inline" src={workingHoursTitleIcon} alt="" />
                      <span>Робочий графік</span>
                    </div>
                    <div className={`sdScheduleHead__status sdScheduleHead__status--${workStatus.kind}`}>{workStatus.label}</div>
                  </div>

                  <div className="sdScheduleHead__right">
                    <span className={`sdScheduleChevron ${scheduleOpen ? "is-open" : ""}`} aria-hidden="true">
                      ▾
                    </span>
                  </div>
                </button>

                <div className={`sdScheduleWrap ${scheduleOpen ? "is-open" : ""}`}>
                  <div className="sdScheduleInner">
                    <div className="sdScheduleList">
                      {WEEK_ORDER.map((day, idx) => {
                        const v = scheduleByDay[day] || null;
                        const from = String(v?.from || "").trim();
                        const to = String(v?.to || "").trim();
                        const right = from || to ? `${from || "—"} - ${to || "—"}` : "Вихідний";

                        return (
                          <div
                            className="sdScheduleLine"
                            key={`sch_${day}`}
                            style={{ transitionDelay: scheduleOpen ? `${Math.min(idx, 10) * 28}ms` : "0ms" }}
                          >
                            <div className="sdScheduleLine__day">{day}</div>
                            <div className={`sdScheduleLine__time ${!(from || to) ? "is-off" : ""}`}>{right}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!!locText && (
              <div className="sdCard">
                <div className="sdCard__head">
                  <div className="sdTitleRow sdTitleRow--noMargin">
                    <img className="sdTitleIcon" src={mapIcon} alt="" />
                    <div className="sdCard__title">Адреса</div>
                  </div>
                </div>

                <div className="sdText">{locText}</div>
                {!!locExtra && (
                  <div className="sdText sdText--muted" style={{ marginTop: 6 }}>
                    {locExtra}
                  </div>
                )}
              </div>
            )}

            {hasPhotos && (
              <div className="sdCard">
                <div className="sdTitleRow">
                  <img className="sdTitleIcon" src={processTitleIcon} alt="" />
                  <div className="sdCard__title">{galleryTitle}</div>
                </div>

                <div className="sdMediaRow" role="list" aria-label="Фото робіт">
                  {showcaseGallery.items.map((p, idx) => {
                    const u = String(p?.url || "").trim();
                    const top = String(p?.top || "").trim();
                    const bottom = String(p?.bottom || "").trim();
                    if (!u) return null;

                    const src = `${u}${u.includes("?") ? "&" : "?"}v=${photoCacheBuster}`;

                    return (
                      <div
                        className="sdMediaItem"
                        key={`ph_${idx}`}
                        role="listitem"
                        onClick={() => {
                          setPhotoStartIndex(idx);
                          setPhotoOpen(true);
                        }}
                        style={{ cursor: "pointer" }}
                        aria-label="Відкрити фото"
                      >
                        <div className="sdMediaThumb">
                          <img className="sdMediaImg" src={src} alt={top || `photo ${idx + 1}`} loading="lazy" />
                        </div>

                        {top || bottom ? (
                          <div className="sdMediaMeta">
                            {top ? <div className="sdMediaTop">{top}</div> : null}
                            {bottom ? <div className="sdMediaBottom">{bottom}</div> : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {hasServices && (
              <div className="sdCard">
                <div className="sdTitleRow">
                  <img className="sdTitleIcon" src={serviceTitleIcon} alt="" />
                  <div className="sdCard__title">Послуги</div>
                </div>

                <div className="sdServicesChips" role="list" aria-label="Послуги">
                  {services.map((s, idx) => {
                    const t = String(s || "").trim();
                    if (!t) return null;
                    return (
                      <div className="sdServiceChip" key={`srv_${idx}`} role="listitem">
                        {t}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {hasExperience ? (
              <div className="sdCard">
                <div className="sdTitleRow">
                  <img className="sdTitleIcon" src={experienceTitleIcon} alt="" />
                  <div className="sdCard__title">Досвід</div>
                </div>

                <div className="sdFormatRow">
                  {experience.startYear ? (
                    <div className="sdFormatPill">
                      Працюю з {experience.startYear}
                      {yearsExp ? ` · ${yearsExp} ${yearsWord(yearsExp)}` : ""}
                    </div>
                  ) : null}

                  {experience.clientsCount ? <div className="sdFormatPill">{experience.clientsCount}+ клієнтів</div> : null}
                </div>
              </div>
            ) : null}

            {modeLabel ? (
              <div className="sdCard">
                <div className="sdTitleRow">
                  <img className="sdTitleIcon" src={formatTitleIcon} alt="" />
                  <div className="sdCard__title">Формат</div>
                </div>

                <div className="sdFormatRow">
                  <div className="sdFormatPill">{modeLabel}</div>
                </div>
              </div>
            ) : null}

            {hasOffers && (
              <div className="sdCard">
                <div className="sdTitleRow">
                  <img className="sdTitleIcon" src={promotionTitleIcon} alt="" />
                  <div className="sdCard__title">Акційні пропозиції</div>
                </div>

                <div className="sdOffersChips" role="list" aria-label="Акційні пропозиції">
                  {offers.map((o, idx) => {
                    const t = String(o?.title || "").trim();
                    const d = String(o?.details || "").trim();
                    if (!t && !d) return null;

                    return (
                      <div className="sdOfferChip" key={`off_${idx}`} role="listitem">
                        {t && <div className="sdOfferChipTitle">{t}</div>}
                        {d && <div className="sdOfferChipDesc">{d}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {hasFaq && (
              <div className="sdCard">
                <div className="sdTitleRow">
                  <img className="sdTitleIcon" src={faqTitleIcon} alt="" />
                  <div className="sdCard__title">Найпопулярніші запитання</div>
                </div>

                <div className="sdFaqAcc">
                  {faq.map((f, idx) => {
                    const q = String(f?.q || "").trim();
                    const a = String(f?.a || "").trim();
                    if (!q && !a) return null;

                    const isOpen = faqOpenIndex === idx;

                    return (
                      <div className={`sdFaqItemAcc ${isOpen ? "is-open" : ""}`} key={`faq_${idx}`}>
                        <button
                          type="button"
                          className="sdFaqQRow"
                          onClick={() => setFaqOpenIndex((prev) => (prev === idx ? -1 : idx))}
                          aria-expanded={isOpen ? "true" : "false"}
                        >
                          <div className="sdFaqQText">{q || "Запитання"}</div>
                          <div className="sdFaqChevron" aria-hidden="true">
                            ▾
                          </div>
                        </button>

                        <div className={`sdFaqAWrap ${isOpen ? "is-open" : ""}`}>
                          <div className="sdFaqAInner">{a ? <div className="sdFaqAText">{a}</div> : null}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}